import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import {
  applyUiStyle,
  isUiStyle,
  resolveUiStyle,
  storeUiStylePreview,
  toUiStyle,
  type UiStyle,
} from '@/config/uiStyle';
import { buildUiStyle, isStyleSwitcherAllowed } from '@/config/uiStyleEnv';
import { loadStyleFonts } from '@/styles/styleFonts';

export interface UiStyleValue {
  style: UiStyle;
  /** Switches the style live (preview builds only; a no-op when the switcher is not allowed). */
  setStyle: (style: UiStyle) => void;
}

const UiStyleContext = createContext<UiStyleValue | null>(null);

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Build value, then (switcher builds only) the page-load `?style=` or the stored preview. */
function resolveFromEnvironment(): UiStyle {
  return resolveUiStyle({
    buildStyle: buildUiStyle(),
    search: globalThis.location?.search ?? '',
    storage: safeLocalStorage(),
    switcherAllowed: isStyleSwitcherAllowed(),
  });
}

/** The style on <html> (set by bootUiStyle), else the resolved one. */
function currentStyle(): UiStyle {
  const applied = typeof document !== 'undefined' ? document.documentElement.dataset.uiStyle : undefined;
  return isUiStyle(applied) ? applied : resolveFromEnvironment();
}

/**
 * Fonts for a style other than the build's. The build style's fonts are in the entry CSS
 * (main.tsx, src/config/uiStyleBuild.ts); only a preview of another style loads its own.
 */
function loadPreviewFonts(style: UiStyle): void {
  if (style !== toUiStyle(buildUiStyle())) void loadStyleFonts(style);
}

/**
 * Pre-render boot (main.tsx): resolves the style and sets `<html data-ui-style>` so the first
 * paint already uses its tokens. A previewed style (switcher builds) starts loading its fonts.
 */
export function bootUiStyle(): UiStyle {
  const style = resolveFromEnvironment();
  if (typeof document !== 'undefined') applyUiStyle(style, document.documentElement);
  if (isStyleSwitcherAllowed()) loadPreviewFonts(style);
  return style;
}

/**
 * The active style for the few style-aware places (background art gate, dev switcher).
 * Components never branch on it: they read the style's tokens.
 */
export function UiStyleProvider({ children }: { children: React.ReactNode }) {
  const [style, setStyleState] = useState<UiStyle>(currentStyle);

  const setStyle = useCallback((next: UiStyle) => {
    if (!isStyleSwitcherAllowed() || !isUiStyle(next)) return;
    // Applied right away (not in an effect) so tokens, fonts and every open dialog switch together.
    applyUiStyle(next, document.documentElement);
    loadPreviewFonts(next);
    storeUiStylePreview(next, safeLocalStorage());
    setStyleState(next);
  }, []);

  // Keeps <html> in step when the provider runs without the boot (tests, isolated renders).
  useLayoutEffect(() => {
    applyUiStyle(style, document.documentElement);
  }, [style]);

  const value = useMemo(() => ({ style, setStyle }), [style, setStyle]);
  return <UiStyleContext.Provider value={value}>{children}</UiStyleContext.Provider>;
}

const noop = () => {};

/** Active style and the preview setter. Outside the provider: the applied style and a no-op setter. */
export function useUiStyle(): UiStyleValue {
  const ctx = useContext(UiStyleContext);
  return ctx ?? { style: currentStyle(), setStyle: noop };
}
